import { useState, useEffect, useRef } from 'react';
import { history, useLocation } from 'umi';
import moment from 'moment';
import { notification } from 'tea-component';
import { getStudioList, getStudioOverviewById, getStudioTrendById } from '@sg/services/index';
import type { GameInfo, MetricsData } from '../types';
import { createBtnPoint, SGBuriedBtnPoint, StudiosTab } from '@/pages/strategic/utils/buriedPoint';

export const useStudioList = () => {
  const location = useLocation();
  const [studioHeaderList, setStudioHeaderList] = useState<any[]>([]);
  const [studioList, setStudioList] = useState<any[]>([]);
  const [hasStudioAccess, setHasStudioAccess] = useState<boolean>(true);
  const [gameInfo, setGameInfo] = useState<GameInfo>({
    name: '',
    unified_id: '',
  });

  const fetchStudioData = async () => {
    const res = await getStudioList({});
    return res?.map((item: any) => ({
      name: item.name,
      cover: item.cover,
      unified_id: item.id,
      originalData: item,
    })) || [];
  };

  const handleStudioChange = (
    studio: any,
    triggerSource: 'auto' | 'click' = 'click',
  ) => {
    // Track buried point only for real user selection
    if (triggerSource === 'click') {
      const studioName = studio?.name || 'Unknown';
      const studioId = studio?.unified_id || '';
      const btnPoint = createBtnPoint(
        StudiosTab,
        'studioSelect',
        `Studio Select - Studio: ${studioName}${studioId ? ` (ID: ${studioId})` : ''}`
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    }
    // URL update is now handled by GameSelect component via urlParamKey prop
    // gameInfo is synced from URL below
  };

  const handleStudioDataLoaded = (data: any[]) => {
    setStudioHeaderList(data);
    setStudioList(data);
    // Check if user has access to any studios after permission filtering
    setHasStudioAccess(data && data.length > 0);
    // GameSelect handles initial selection based on defaultUnifiedId prop
  };

  // Synchronize gameInfo from URL parameters and studioList
  // This ensures gameInfo only changes when URL actually changes, enabling RoutePrompt interception
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlStudioId = searchParams.get('studio_id');
    const currentSubtitle = searchParams.get('subtitle');

    if (currentSubtitle !== 'studio') {
      return;
    }

    if (urlStudioId && studioList.length > 0) {
      const studioFromUrl = studioList.find((studio: any) => studio.unified_id === urlStudioId);
      if (studioFromUrl) {
        setGameInfo({
          name: studioFromUrl.name,
          unified_id: studioFromUrl.unified_id,
        });
      }
    } else if (!urlStudioId && studioList.length > 0) {
      const firstStudio = studioList[0];
      if (firstStudio) {
        setGameInfo({
          name: firstStudio.name,
          unified_id: firstStudio.unified_id,
        });
      }
    }
  }, [location.search, studioList]);

  return {
    studioHeaderList,
    gameInfo,
    hasStudioAccess,
    fetchStudioData,
    handleStudioChange,
    handleStudioDataLoaded,
  };
};

export const useStudioOverview = (studioId: string, selectedMonth?: string | null) => {
  const [studioOverviewData, setStudioOverviewData] = useState<any>(null);
  const [studioOverviewDataTime, setStudioOverviewDataTime] = useState<string | null>(null);

  useEffect(() => {
    if (!studioId) {
      // Reset state when no studioId
      setStudioOverviewData(null);
      setStudioOverviewDataTime(null);
      return;
    }

    const fetchData = async () => {
      try {
        // Reset state before fetching to ensure clean state when switching studios
        setStudioOverviewData(null);
        setStudioOverviewDataTime(null);
        
        // Build payload with optional datatime parameter (already formatted as "YYYY-MM")
        const payload: { studio_id: string; datatime?: string } = { studio_id: studioId };
        if (selectedMonth) {
          payload.datatime = selectedMonth;
        }
        
        const response = await getStudioOverviewById(payload);
        const overviewData = response?.data;
        if (overviewData) {
          setStudioOverviewData(overviewData);
          
          // Extract datatime from response
          const datatime = overviewData?.profit?.datatime;
          if (datatime) {
            setStudioOverviewDataTime(datatime);
          }
          // If no datatime, studioOverviewDataTime remains null (already reset above)
        }
      } catch (error) {
        console.error('Error fetching studio overview data:', error);
        notification.error({
          description: 'Failed to fetch studio overview data'
        });
        setStudioOverviewData(null);
        setStudioOverviewDataTime(null);
      }
    };

    fetchData();
  }, [studioId, selectedMonth]);

  return { studioOverviewData, studioOverviewDataTime };
};

export const useStudioTrend = (studioId: string, activeCard: string, selectedMonth?: string | null) => {
  const [revenueData, setRevenueData] = useState<any>([]);
  const [trendLoading, setTrendLoading] = useState<boolean>(false);
  const trendRequestIdRef = useRef(0);
  
  const [currentMetricsData, setCurrentMetricsData] = useState<MetricsData>({
    actual: {
      title: 'Monthly Actual',
      date: '',
      data: null,
      content: 'MoM',
      rate: ''
    },
    // forecast: {
    //   title: 'Monthly Forecast',
    //   date: '',
    //   data: null,
    //   content: 'MoM',
    //   rate: ''
    // },
    kpi: {
      title: 'Monthly KPI',
      date: '',
      data: null,
      content: 'KPI Completion',
      rate: ''
    },
  });

  const resetCurrentMetricsData = () => {
    setCurrentMetricsData({
      actual: {
        title: 'Monthly Actual',
        date: '',
        data: null,
        content: 'MoM',
        rate: ''
      },
      kpi: {
        title: 'Monthly KPI',
        date: '',
        data: null,
        content: 'KPI Completion',
        rate: ''
      },
    });
  };

  useEffect(() => {
    if (!studioId) {
      setRevenueData([]);
      resetCurrentMetricsData();
      setTrendLoading(false);
      return;
    }

    const fetchData = async () => {
      const requestId = ++trendRequestIdRef.current;
      try {
        setTrendLoading(true);
        // Clear current trend data immediately to avoid stale flash while switching card/studio.
        setRevenueData([]);
        resetCurrentMetricsData();

        // Build payload with type and optional datatime parameter (already formatted as "YYYY-MM")
        const type: 'revenue' | 'profit' = activeCard === 'Profit' ? 'profit' : 'revenue';
        const payload: { studio_id: string; type: 'revenue' | 'profit'; datatime?: string } = { studio_id: studioId, type };
        if (selectedMonth) {
          payload.datatime = selectedMonth;
        }
        
        const res: any = await getStudioTrendById(payload);
        if (requestId !== trendRequestIdRef.current) {
          return;
        }
        const trendData = res?.data?.data || [];
        
        // New API returns flat data per type — map directly
        const transformedData = trendData.map((monthData: any) => ({
          month: monthData.month,
          actual: monthData.actual,
          forecast: monthData.forecast,
          kpi: monthData.kpi,
          kpi_completion: monthData.kpi_completion,
          mom_actual: monthData.mom_actual,
          mom_forecast: monthData.mom_forecast,
          projects: monthData.projects || [],
        }));
        
        setRevenueData(transformedData);
        
        const lastValidData = transformedData.findLast((item: any) => item.actual !== null);
        
        if (lastValidData) {
          setCurrentMetricsData({
            actual: {
              title: 'Monthly Actual',
              date: lastValidData.month,
              data: lastValidData.actual,
              content: 'MoM',
              rate: lastValidData.mom_actual?.toFixed(1) || ''
            },
            // forecast: {
            //   title: 'Monthly Forecast',
            //   date: lastValidData.month,
            //   data: lastValidData.forecast,
            //   content: 'MoM',
            //   rate: lastValidData.mom_forecast?.toFixed(1) || ''
            // },
            kpi: {
              title: 'Monthly KPI',
              date: lastValidData.month,
              data: lastValidData.kpi,
              content: 'KPI Completion',
              rate: lastValidData.kpi_completion?.toFixed(1) || ''
            },
          });
        } else {
          // Reset to initial empty state when there's no valid data
          resetCurrentMetricsData();
        }
      } catch (error) {
        if (requestId !== trendRequestIdRef.current) {
          return;
        }
        console.error('Error fetching studio trend data:', error);
        notification.error({
          description: 'Failed to fetch studio trend data'
        });
        setRevenueData([]);
        resetCurrentMetricsData();
      } finally {
        if (requestId === trendRequestIdRef.current) {
          setTrendLoading(false);
        }
      }
    };

    fetchData();
  }, [studioId, activeCard, selectedMonth]);

  return { revenueData, currentMetricsData, trendLoading };
};

